import type { WidgetMessages } from './types.ts';

export const en: WidgetMessages = {
  panelTitle: 'Send feedback',
  panelClose: 'Close',
  fieldTitleLabel: 'Brief Summary',
  fieldTitlePlaceholder: 'Short description',
  fieldSummaryLabel: 'Details',
  fieldSummaryPlaceholder: 'Send feedback, describe a bug, etc...',
  fieldNameLabel: 'Your name',
  fieldEmailLabel: 'Your email',
  fieldSaveContactLabel: 'Save your name and email',
  fieldSaveContactUnavailable: "Your browser is blocking storage, so this can't be saved.",
  fieldNotifyOnFixLabel: 'Email me when this is fixed',
  buttonSend: 'Send report',
  buttonSending: 'Sending…',
  buttonWaitingForUploads: 'Waiting for attachments…',
  errorTitleRequired: 'Please add a brief summary.',
  successHeading: 'Report received',
  successCopy: (ref) => `Reference code: ${ref}`,
  successPartialNotice: (failed, total) =>
    failed === total
      ? failed === 1
        ? "Your attachment couldn't be processed and wasn't included with your report."
        : `All ${total} attachments couldn't be processed and weren't included with your report.`
      : `${failed} of ${total} attachments couldn't be processed and weren't included with your report.`,
  successAnotherButton: 'Report another',
  poweredBy: 'Powered by OnCatch',

  recordingConsentTitle: 'Record your screen',
  recordingConsentLead: (ws) =>
    `You're about to share your screen with ${ws}'s support team to demonstrate a bug.`,
  recordingConsentBulletAdminsOnly: 'The recording is sent only to admins on your workspace.',
  recordingConsentBulletMicAudio:
    "Microphone audio is captured if your browser supports it; you'll see a mic prompt.",
  recordingConsentBulletMaxLength: 'Maximum length is 5 minutes — recording auto-stops at the cap.',
  recordingConsentBulletRetention: (d) => `Retained for ${d} days, then automatically deleted.`,
  recordingConsentBulletDeletion: 'You can request earlier deletion via your account settings.',
  recordingConsentCancelBtn: 'Cancel',
  recordingConsentAcceptBtn: 'I agree, start recording',
  recordingChoiceTitle: 'Choose a recording',
  recordingChoiceLead:
    'Choose what you want to capture. You will review what is shared before recording starts.',
  recordingChoiceScreen: 'Screen recording',
  recordingChoiceScreenDetail: 'Record your screen and optional microphone audio.',
  recordingChoiceCamera: 'Screen recording with camera',
  recordingChoiceCameraDetail: 'Record your screen with a camera overlay.',
  recordingChoiceAudio: 'Audio recording',
  recordingChoiceAudioDetail: 'Record only your microphone audio.',
  recordingChoiceUnavailable: 'Not available in this browser',
  recordingAudioConsentTitle: 'Record audio',
  recordingAudioConsentLead: (ws) =>
    `Your microphone audio will be shared with the support team for ${ws} to help explain this issue.`,
  recordingAudioConsentTip:
    'Your browser will ask for microphone access next. Choose the microphone you want to use, then allow access.',

  recordingTimerIdle: 'Recording 0:00',
  recordingTimer: (m, s) => `Recording ${m}:${String(s).padStart(2, '0')}`,
  recordingStopBtn: 'Stop',
  recordingStartedAnnouncement: 'Recording started',
  recordingProgressAnnouncement: (sec) => `Recording ${sec} seconds`,

  captureRegionHelp: 'Click and drag to select a region · Press Esc to cancel',
  captureLabelCapturing: 'Capturing…',
  captureLabelPreparing: 'Preparing…',
  captureRegionCapturing: 'Capturing region…',

  safariUnsupportedTitle: "Screen recording isn't supported on this browser",
  safariUnsupportedBody:
    'Screen recording requires desktop Chrome, Edge, or Firefox. You can still attach screenshots and submit your report.',

  attachmentsLabel: 'Attachments',
  captureWholePageBtn: 'Whole page',
  captureSelectRegionBtn: 'Select region',
  captureSelectElementBtn: 'Select element',
  captureRecordBtn: 'Record',
  cameraDeniedFallback: 'Camera access denied — recording screen only.',
  recordingConsentBulletCamera:
    'Your camera feed will be overlaid in the corner of the recording. Decline the camera prompt to record screen only.',
  recordingConsentIncludeCameraLabel: 'Include camera',
  recordingUnsupportedInline:
    "Screen recording isn't supported in this browser. Screenshots still work.",

  // ── Upload dropzone (#605) ───────────────────────────────────────────
  uploadDropzoneBtn: 'Upload file',
  uploadDropzoneHint: () => 'Image, text, html, markdown, etc',
  uploadDropzoneCapReached: 'Attachment limit reached — remove one to upload.',
  uploadErrorUnsupportedType: 'Only PNG, JPEG, WebP, PDF, or DOCX files can be uploaded.',
  uploadErrorFileTooLarge: 'File too large (max 50 MB).',
  uploadErrorGeneric: 'Upload failed. Please try again.',
  uploadErrorTooMany: (skipped) =>
    skipped === 1
      ? '1 file skipped — attachment limit reached.'
      : `${skipped} files skipped — attachment limit reached.`,

  storageFullNotice: 'Attachments unavailable',
  storageFullDetailedNotice: 'Attachments unavailable — workspace storage full',

  challengeIntro: 'Quick check to confirm you are human.',
  challengeFailed: 'Verification failed. Please try again.',

  annotateTitle: 'Annotate screenshot',
  toolArrow: 'Arrow',
  toolBox: 'Box',
  toolPen: 'Draw',
  toolText: 'Text',
  colorRed: 'Red',
  colorYellow: 'Yellow',
  colorGreen: 'Green',
  colorBlack: 'Black',
  buttonUndo: 'Undo',
  buttonClear: 'Clear',
  buttonSkip: 'Skip',
  buttonSave: 'Save',
  textInputPlaceholder: 'Type label, press Enter',
  saveError: "Couldn't save annotation. Using raw screenshot.",
  activeIndicator: 'active',
};
